import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { GenericValidationsService } from 'src/app/Service/generic-validations.service';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';
import { CONSTANTS } from 'src/Core/Constant';
import { environment } from 'src/environments/environment';
import { DocumentUploadConfig } from '../../addendum-document-upload/addendum-document-upload.component';
import { TranslationService } from 'src/app/Service/translation.service';

@Component({
  selector: 'app-contemplation',
  templateUrl: './contemplation.component.html',
  styleUrls: ['./contemplation.component.scss']
})
export class ContemplationComponent {
  @Output() formGroupChange = new EventEmitter<FormGroup>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();
  @Input() isPreviewMode: boolean = false; // Flag to determine behavior
  @Input() applicantData: any; // Accept data from parent component

  // Document upload properties
  DocumentType = CONSTANTS.DocumentType;
  documents: any[] = [];

  // Document upload configuration
  documentUploadConfig: DocumentUploadConfig = {
    title: 'VS Document Upload',
    description: 'Please upload your VS Document. Supported formats: PDF, JPEG, PNG. Maximum file size: 5MB.',
    uploadButtonText: 'Upload VS Document',
    documentTypeLabel: 'Visiting Seva Document',
    acceptedFormats: '.pdf,.jpg,.jpeg,.png',
    validationErrorTitle: 'Document Required',
    validationErrorMessage: 'Please upload at least one document before proceeding to the next step.',
    documentType: CONSTANTS.DocumentType.VSDoc,
    maxFileSize: 5 * 1024 * 1024, // 5MB in bytes
    allowedFormats: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
    endpoints: {
      upload: 'FileHandler/UploadFile',
      getAll: 'FileHandler/GetAllDocuments',
      delete: 'FileHandler/DeleteFileById?id=',
      saveParentAddendum: 'VSApplication/SaveVSAddendum'
    },
  };





  isJsonShow = environment.isJsonShow;  // Set isJsonShow from the environment
  siddhaYogaForm!: FormGroup;
  submitted = false;
  documentSectionSubmitted = false; // Track document section validation
  SaveMode = CONSTANTS.SAVE_MODE;
  isEditable: boolean = true; // Flag to track if the form is editable
  // applicationGuid: any;

  // Letter count properties
  characterCounts = {
    TeachingsPracticeInDetails: 0,
    WebsitePracticeInDetails: 0,
    TeachingsPracticeBenifitsInDetails: 0,
    WebsitePracticeBenifitsInDetails: 0,
    AppliedQualitiesInSeva: 0,
    DailyScheduleDetails: 0,
    SevaBenifitForTeachings: 0
  };


  constructor(
    private activatedRoute: ActivatedRoute, private router: Router,
     private fb: FormBuilder, private toastr: ToastrService,
      private api: SevaProfileService,
       public genericValidations: GenericValidationsService,
       private translationService: TranslationService) {
  }

  // Letter counting methods
  countLetters(text: string): number {
    if (!text) return 0;
    return text.length;
  }

  updateLetterCount(fieldName: string, value: string): void {
    this.characterCounts[fieldName as keyof typeof this.characterCounts] = this.countLetters(value);
  }

  getCharacterCount(fieldName: string): number {
    return this.characterCounts[fieldName as keyof typeof this.characterCounts];
  }

  // Check if document section is valid
  isDocumentSectionValid(): boolean {
    return this.documents && this.documents.length > 0;
  }

  // Event handlers for generic document upload component
  onDocumentsChange(documents: any[]): void {
    this.documents = documents;
  }

  onValidationChange(isValid: boolean): void {
    // This can be used to track validation state if needed
    console.log('Document validation state:', isValid);
  }

  // Load documents for preview mode
  loadDocumentsForPreview(): void {
    if (this.isPreviewMode && this.applicantData && this.applicantData.documents) {
      this.documents = this.applicantData.documents;
    }
  }

  ngOnInit(): void {
    if (!this.isPreviewMode) {
      this.initializeForm();
      this.getApplicantData();
      // Note: Document loading is now handled by the generic addendum-document-upload component
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['applicantData'] && this.applicantData) {

      this.initializeForm();
      this.siddhaYogaForm.patchValue(this.applicantData);
      this.siddhaYogaForm.disable();

      // Initialize word counts for preview mode
      this.updateLetterCount('TeachingsPracticeInDetails', this.applicantData.TeachingsPracticeInDetails || '');
      this.updateLetterCount('WebsitePracticeInDetails', this.applicantData.WebsitePracticeInDetails || '');
      this.updateLetterCount('TeachingsPracticeBenifitsInDetails', this.applicantData.TeachingsPracticeBenifitsInDetails || '');
      this.updateLetterCount('WebsitePracticeBenifitsInDetails', this.applicantData.WebsitePracticeBenifitsInDetails || '');
      this.updateLetterCount('AppliedQualitiesInSeva', this.applicantData.AppliedQualitiesInSeva || '');
      this.updateLetterCount('DailyScheduleDetails', this.applicantData.DailyScheduleDetails || '');
      this.updateLetterCount('SevaBenifitForTeachings', this.applicantData.SevaBenifitForTeachings || '');
      // Load documents for preview mode
      this.loadDocumentsForPreview();
    }
  }


  getApplicantData() {
    this.api.apiCall('VSApplication/GetVSAddendum', 'GET').subscribe((response: any) => {
      if (response.Data && Object.keys(response.Data).length > 0) {
        this.genericValidations.setProgressPercentage(response.Data.CompletionPercentage); // Example: Set progress to 50%
        
        // Store IsEditable flag from response
        this.isEditable = response.Data.IsEditable !== false; // Default to true if not provided

        // Disable form if not editable
        if (!this.isEditable) {
          this.siddhaYogaForm.disable();
        } else {
          this.siddhaYogaForm.enable();
        }
        
        this.siddhaYogaForm.patchValue(response.Data);

        // Initialize word counts for existing data
        this.updateLetterCount('TeachingsPracticeInDetails', response.Data.TeachingsPracticeInDetails || '');
        this.updateLetterCount('WebsitePracticeInDetails', response.Data.WebsitePracticeInDetails || '');
        this.updateLetterCount('TeachingsPracticeBenifitsInDetails', response.Data.TeachingsPracticeBenifitsInDetails || '');
        this.updateLetterCount('WebsitePracticeBenifitsInDetails', response.Data.WebsitePracticeBenifitsInDetails || '');
        this.updateLetterCount('AppliedQualitiesInSeva', response.Data.AppliedQualitiesInSeva || '');
        this.updateLetterCount('DailyScheduleDetails', response.Data.DailyScheduleDetails || '');
        this.updateLetterCount('SevaBenifitForTeachings', response.Data.SevaBenifitForTeachings || '');
      }
    })
  }


  initializeForm() {


    // this.activatedRoute.queryParamMap.subscribe(params => {
    //   this.applicationGuid = params.get('applicationGuid')!;
    //   console.log('Application Guid:', this.applicationGuid);
    // });



    this.siddhaYogaForm = this.fb.group({
      // ApplicationGuid: [ this.applicationGuid , Validators.required],

      SubmissionMode: ['', Validators.required],
      SaveMode: ['', Validators.required],

      TeachingsPracticeInDetails: ['', [Validators.required, Validators.minLength(100), Validators.maxLength(5000)]],  // Q1A
      WebsitePracticeInDetails: ['', [Validators.required, Validators.minLength(100), Validators.maxLength(5000)]],   // Q1B
      TeachingsPracticeBenifitsInDetails: ['', [Validators.required, Validators.minLength(100), Validators.maxLength(5000)]], // Q2A
      WebsitePracticeBenifitsInDetails: ['', [Validators.required, Validators.minLength(100), Validators.maxLength(5000)]],   // Q2B
      AppliedQualitiesInSeva: ['', [Validators.required, Validators.minLength(100), Validators.maxLength(5000)]], // Q3
      DailyScheduleDetails: ['', [Validators.required, Validators.minLength(100), Validators.maxLength(5000)]], // Q2A
      SevaBenifitForTeachings: ['', [Validators.required, Validators.minLength(100), Validators.maxLength(5000)]], // Q5
    });





    this.formGroupChange.emit(this.siddhaYogaForm);
  }

  onSubmit(Mode: number): void {

    this.siddhaYogaForm.patchValue({
      SaveMode: Mode
    })
 

    let submissionType = this.siddhaYogaForm.get("SubmissionMode")?.value

    if (submissionType == 'file') {
      // Validate document section
      this.documentSectionSubmitted = true;
      
      if (!this.documents || this.documents.length === 0) {
        this.toastr.error('Please upload at least one document before proceeding.');
        return;
      } else {
        const cleanedValues = this.genericValidations.removeWhitespaceFromValues(this.siddhaYogaForm.value);
        this.addUpdate(cleanedValues, Mode);
      }
    }

    else {

      if (!this.siddhaYogaForm.valid && Mode == this.SaveMode.SAVE) {
        this.submitted = true;
        this.siddhaYogaForm.markAllAsTouched();
        this.toastr.warning('Please complete all required fields before submitting.');
        return
      }
      const cleanedValues = this.genericValidations.removeWhitespaceFromValues(this.siddhaYogaForm.value);
      this.addUpdate(cleanedValues, Mode);
    }
  }


  addUpdate(reqBoby: any, Mode: number) {
    this.api.apiCall('VSApplication/SaveVSAddendum', 'POST', reqBoby).subscribe((response: any) => {
      this.toastr.success(response?.Message);
      if (Mode == this.SaveMode.SAVE) {
        this.nextStep.emit();
      } else if (Mode == this.SaveMode.DRAFT) {
        this.getApplicantData();
      }
    });
  }

  goToBack() {
    this.previous.emit();
  }

  // Translations
  getTranslation(key: string): string {
    return this.translationService.getTranslation(key);
  }


}